<template>
  <div id="kiosk-terms-page" class="row w-100 justify-content-center">
    <div class="col-12 col-md-10 col-lg-9 text-left">
      <div v-if="loadingKioskContractTemplate" class="text-muted p2">Loading terms…</div>
      <div v-else-if="kioskContractTemplateError" class="text-danger p2">{{ kioskContractTemplateError }}</div>
      <div v-else-if="kioskContractTemplate">
        <div class="font-weight-bold mb-2" style="color:var(--primary-color); letter-spacing:1.4px; font-size:.75rem; text-transform:uppercase;">
          <i class="fa fas fa-shield-alt mr-1"></i>
          Terms &amp; Conditions
          <span v-if="kioskContractTemplate.requiresSignature">&middot; Signature required</span>
        </div>
        <h1 class="mb-2">Almost done!</h1>
        <p class="mb-1">Signing as <b>{{ group.clientFullName }}</b></p>
        <p class="mb-4 text-muted">{{ signingDateTime }}</p>
        <div class="bg-white p-4 kiosk-contract-viewer" style="border:1px solid #EEE; border-radius:14px; overflow:auto;" v-html="sanitizedHtml"></div>
        <div class="d-flex align-items-center mt-3 p-3" :style="acceptStyle">
          <input type="checkbox" v-model="accepted" id="kiosk-accept" class="mr-3" style="transform:scale(1.4);">
          <label for="kiosk-accept" class="font-weight-bold mb-0">I have read and accept the terms above.</label>
        </div>
        <div v-if="requiresSignature" class="mt-4">
          <div class="font-weight-bold p2 mb-2">{{ signatureMode === 'drawn' ? 'PLEASE SIGN BELOW' : 'PLEASE TYPE YOUR FULL NAME' }}</div>
          <signature-pad v-if="signatureMode === 'drawn'" ref="sigPad" v-model="signatureText" class="mb-2"></signature-pad>
          <input v-else type="text" class="form-control kiosk-typed-signature mb-2"
                 v-model="signatureText"
                 :placeholder="group.clientFullName"
                 autocomplete="off"/>
          <div class="d-flex justify-content-between align-items-center mt-0">
            <a href="#" class="kiosk-switch-mode" @click.prevent="clearSignature">
              <i class="fa fas fa-eraser mr-1"></i>Clear
            </a>
            <a href="#" class="kiosk-switch-mode" @click.prevent="switchSignatureMode">
              <i :class="signatureMode === 'drawn' ? 'fa fas fa-keyboard mr-1' : 'fa fas fa-pencil-alt mr-1'"></i>
              {{ signatureMode === 'drawn' ? 'Type Name' : 'Sign Name' }}
            </a>
          </div>
        </div>
        <div v-if="error" class="text-danger p2 mt-3">{{ error }}</div>
        <div class="d-flex justify-content-between align-items-center mt-4">
          <button type="button" class="btn btn-outline-secondary" @click="$emit('back')">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="!canSubmit || submitting" @click="submit">
            {{ submitting ? 'Submitting…' : 'Accept &amp; Check In' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify'
import moment from 'moment-timezone'
import { mapState } from 'vuex'
import { WebApi } from '@/services/WebApi'
import { ErrorHelper } from '@/helpers/ErrorHelper'
import { KioskContractTemplateCacheMixin } from '@/mixins/Kiosk/KioskContractTemplateCacheMixin'
import KioskSignaturePad from './KioskSignaturePad.vue'

export default {
  name: 'KioskTerms',
  mixins: [KioskContractTemplateCacheMixin],
  props: {
    group: { type: Object, required: true }
  },
  data () {
    return { accepted: false, signatureText: '', submitting: false, error: '', signatureMode: this.detectSignatureMode(), signingNow: new Date(), nowTicker: null }
  },
  computed: {
    sanitizedHtml () { return DOMPurify.sanitize((this.kioskContractTemplate && this.kioskContractTemplate.html) || '') },
    requiresSignature () { return !!(this.kioskContractTemplate && this.kioskContractTemplate.requiresSignature) },
    canSubmit () { return this.accepted && (!this.requiresSignature || !!this.signatureText) },
    acceptStyle () {
      return this.accepted
        ? 'background:#FFF6F8; border:1px solid #F8D0D8; border-radius:14px;'
        : 'background:#FAFAFA; border:1px solid var(--form-color); border-radius:14px;'
    },
    ...mapState('TimeZone', ['timeZone']),
    signingDateTime () {
      try {
        const zone = (this.timeZone && this.timeZone.timeZoneId) || moment.tz.guess()
        return moment(this.signingNow).tz(zone).format('MMMM D, YYYY [at] h:mm A')
      } catch (e) { return '' }
    }
  },
  created () {
    this.fetchKioskContractTemplate()
    this.nowTicker = setInterval(() => { this.signingNow = new Date() }, 30000)
  },
  beforeDestroy () {
    if (this.nowTicker) clearInterval(this.nowTicker)
  },
  methods: {
    detectSignatureMode () {
      const coarse = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches
      return coarse ? 'drawn' : 'typed'
    },
    clearSignature () {
      if (this.signatureMode === 'drawn' && this.$refs.sigPad && typeof this.$refs.sigPad.clear === 'function') {
        this.$refs.sigPad.clear()
      }
      this.signatureText = ''
    },
    switchSignatureMode () {
      this.signatureMode = this.signatureMode === 'drawn' ? 'typed' : 'drawn'
      this.signatureText = ''
    },
    submit () {
      const self = this
      self.submitting = true
      self.error = ''
      const payload = {
        appointmentGroupId: self.group.appointmentGroupId,
        clientId: self.group.clientId,
        signatureText: self.requiresSignature ? self.signatureText : null
      }
      WebApi.post('Kiosk/SignIn', payload)
        .then(response => { self.$emit('signed-in', response.data) })
        .catch(error => {
          const status = error.response && error.response.status
          if (status === 401 || status === 403) {
            self.$emit('unauthorized')
          } else {
            self.error = ErrorHelper.getError(error)
          }
        })
        .finally(() => { self.submitting = false })
    }
  },
  components: { 'signature-pad': KioskSignaturePad }
}
</script>

<style scoped>
#kiosk-terms-page .kiosk-typed-signature {
  background: #FFEDB2;
  border: 1px dashed var(--form-color);
  border-radius: 14px;
  font-family: 'Brush Script MT', cursive;
  font-size: 2rem;
  height: 80px;
  padding: 0 1rem;
}
#kiosk-terms-page .kiosk-switch-mode {
  font-size: .9375rem;
  color: var(--primary-color);
  text-decoration: none;
}
#kiosk-terms-page .kiosk-switch-mode:hover {
  text-decoration: underline;
}
#kiosk-terms-page h1 {
  font-size: 1.125rem !important;
}
#kiosk-terms-page .kiosk-contract-viewer {
  max-height: 480px;
}
@media (max-width: 576px) {
  #kiosk-terms-page h1 { font-size: 1.375rem !important; }
  #kiosk-terms-page p,
  #kiosk-terms-page .p2 { font-size: .8125rem; }
  #kiosk-terms-page label[for="kiosk-accept"] { font-size: .8125rem; }
  #kiosk-terms-page .kiosk-contract-viewer { font-size: .6875rem !important; max-height: 380px; line-height: 1.35; }
  #kiosk-terms-page .kiosk-contract-viewer ::v-deep * { font-size: .6875rem !important; line-height: 1.35 !important; }
  #kiosk-terms-page .kiosk-contract-viewer ::v-deep h1,
  #kiosk-terms-page .kiosk-contract-viewer ::v-deep h2,
  #kiosk-terms-page .kiosk-contract-viewer ::v-deep h3 { font-size: .8125rem !important; }
}
</style>
